import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useRenderCounter } from "@/lib/loop-detector";
import { Trash2, Plus, Link2, CopyPlus, BookOpen, FolderOpen, Search, X, AlertTriangle, Pencil, Check } from "lucide-react";
import { formatAmount as euFormat } from "@/lib/formatters";
import { GermanNumberInput } from "@/components/ui/german-number-input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { ManualMenge } from "@/components/project/area-measurement";
import type { LibraryPosition } from "@shared/schema";
import { pickPriceForQuantity } from "@shared/library-pricing";

function pickLibraryEp(lp: LibraryPosition, qty: number): number {
  const samples = lp.priceSamples ?? [];
  if (samples.length === 0) return lp.ep;
  const pick = pickPriceForQuantity(samples, qty, lp.ep);
  return pick.ep > 0 ? Math.round(pick.ep * 100) / 100 : lp.ep;
}

/**
 * Wendet eine Mengenänderung auf eine LV-Position an. Wenn die Position
 * eine Library-Zeile ist (`lib-…`) und der Nutzer den EP NICHT manuell
 * überschrieben hat, wird der EP zusätzlich mengen-abhängig aus den
 * Preis-Samples der Bibliothek neu gezogen (`pickLibraryEp`). Sonst wird
 * nur die Menge aktualisiert.
 *
 * Reine Funktion — verwendet die übergebenen `libraryPositions`/`manualEpIds`
 * als Snapshot, damit Caller keine Effect-Abhängigkeit von `sektionen`
 * brauchen.
 */
function applyQtyWithLibraryRepricing(
  p: LVPosition,
  newQty: number,
  libraryPositions: LibraryPosition[],
  manualEpIds: Set<string>,
): LVPosition {
  if (p.qty === newQty) return p;
  if (!p.library || manualEpIds.has(p.id) || !p.id.startsWith("lib-")) {
    return { ...p, qty: newQty };
  }
  const libIdNum = Number(p.id.replace(/^lib-/, ""));
  if (!Number.isInteger(libIdNum)) return { ...p, qty: newQty };
  const lp = libraryPositions.find((l) => l.id === libIdNum);
  if (!lp) return { ...p, qty: newQty };
  return { ...p, qty: newQty, ep: pickLibraryEp(lp, newQty) };
}

function pickLibraryEpInfo(lp: LibraryPosition, qty: number): {
  ep: number;
  matchedMenge: number | null;
  source: "sample" | "median" | "fallback";
  sampleCount: number;
} {
  const samples = lp.priceSamples ?? [];
  if (samples.length === 0) {
    return { ep: lp.ep, matchedMenge: null, source: "fallback", sampleCount: 0 };
  }
  const pick = pickPriceForQuantity(samples, qty, lp.ep);
  return {
    ep: pick.ep > 0 ? Math.round(pick.ep * 100) / 100 : lp.ep,
    matchedMenge: pick.matchedMenge,
    source: pick.source,
    sampleCount: samples.length,
  };
}

export interface ProjectCustomPosition {
  id: string;
  sektionId: string;
  name: string;
  unit: string;
  ep: number;
  qty: number;
  aktiv: boolean;
  tag?: string | null;
  beschreibung?: string | null;
}

export interface CustomPositionTemplate {
  projectId: number;
  projectName: string;
  sektionId: string;
  name: string;
  unit: string;
  ep: number;
  qty: number;
  tag?: string | null;
}

export interface LVPosition {
  id: string;
  name: string;
  unit: string;
  ep: number;
  qty: number;
  aktiv: boolean;
  tag?: "AZ" | "ALT" | string;
  variant?: boolean;
  library?: boolean;
  beschreibung?: string | null;
}

export interface LVSektion {
  id: string;
  icon: string;
  titel: string;
  positionen: LVPosition[];
}

export interface LVSummen {
  netto: number;
  mwst: number;
  brutto: number;
}

export interface AnnotatedMenge extends ManualMenge {
  imageIndex: number;
  imageName: string;
}

export interface MengenRef {
  mengeId: string;
  imageIndex: number;
  sourceLabel: string;
}

export type MengenRefMap = Record<string, MengenRef>;

/** Detail-Beitrag eines einzelnen Fotos zu einer LV-Position. */
export interface MengenBreakdownEntry {
  photoId: string;
  photoIndex: number;
  photoName: string;
  /** Vom Nutzer übernommene / bestätigte Menge dieses Fotos für diese Position. */
  qty: number;
  /** Aus Aufmaß errechneter Vorschlag (optional). */
  berechneteMenge?: number;
  /** Optionale Vorschau-URL des Fotos (Data-URL oder URL) für Thumbnail-Anzeige. */
  previewUrl?: string;
}

/** Pro LV-Position-ID eine Liste aller Foto-Beiträge. */
export type MengenBreakdownMap = Record<string, MengenBreakdownEntry[]>;

export interface LVKalkulatorProps {
  onPositionenChange?: (
    aktivePositionen: LVPosition[],
    summen: LVSummen,
    mengenRefs: MengenRefMap
  ) => void;
  onMengenRefsChange?: (refs: Record<string, MengenRef>) => void;
  initialMengen?: Record<string, number>;
  initialMengenRefs?: MengenRefMap;
  /**
   * Pro Position-ID eine automatisch errechnete Menge (z.B. Aufmaß-Gesamtfläche).
   * Setzt nur die Menge, nicht das aktiv-Flag — der Nutzer aktiviert die
   * Position weiterhin manuell. Sobald die Menge im Input geändert wird,
   * meldet die Komponente das via `onMengeManuallyChanged`, damit der Parent
   * die ID aus seiner Auto-Fill-Liste entfernen kann.
   */
  autoFilledMengen?: Record<string, number>;
  onMengeManuallyChanged?: (positionId: string) => void;
  availableMengen?: AnnotatedMenge[];
  /** Pro Position: detaillierter Foto-Breakdown (Quelle der Mengen). */
  mengenBreakdown?: MengenBreakdownMap;
  /**
   * Zusätzliche Einheits-Varianten aus den Per-Foto-Daten, die als eigenständige
   * Zeilen unter der jeweiligen Stamm-Position injiziert werden sollen
   * (z.B. dieselbe Stamm-Position einmal in m² und einmal in lfm).
   * Werden im selben Sektion-Bereich wie die Basis-Position eingefügt und
   * zählen NORMAL zur Summe (im Gegensatz zu Vergleichsvarianten).
   */
  injectedExtras?: Array<{ baseId: string; unit: string; ep: number; qty: number }>;
  /** Optional EP-Overrides für Stamm-Positionen (z.B. mengen-gewichteter
   *  Durchschnitts-EP aus Per-Foto-Daten). */
  initialEpOverrides?: Record<string, number>;
  /**
   * Vom Projekt persistierte eigene Positionen (custom-…). Werden beim Mount
   * in die jeweilige Sektion injiziert, sodass Bezeichnung/Einheit/EP nach
   * einem Reload erhalten bleiben.
   */
  initialCustomPositionen?: ProjectCustomPosition[];
  /**
   * Wird aufgerufen, wenn sich die Liste der eigenen Positionen ändert
   * (Hinzufügen, Bearbeiten, Entfernen, Aktiv-Toggle). Enthält ALLE eigenen
   * Positionen — auch deaktivierte —, damit der Parent sie vollständig
   * persistieren kann.
   */
  onCustomPositionenChange?: (positionen: ProjectCustomPosition[]) => void;
  /**
   * IDs von Positionen, deren EP der Nutzer manuell gesetzt hat. Für solche
   * Zeilen wird der EP NICHT aus der Bibliothek bei Mengen-Änderungen
   * neu gezogen.
   */
  initialManualEpIds?: string[];
  /** Wird gerufen, wenn sich die Liste der manuell überschriebenen EPs ändert. */
  onManualEpIdsChange?: (ids: string[]) => void;
  /**
   * Aktuelle Projekt-ID (numerisch). Wird beim Abruf der „Aus Projekt
   * übernehmen"-Vorlagen verwendet, um Positionen aus dem aktuellen Projekt
   * herauszufiltern.
   */
  currentProjectId?: number;
  highlightPositionId?: string | null;
  onHighlightConsumed?: () => void;
  onGoToMeasurementPhoto?: (imageIndex: number) => void;
  /**
   * Pro LV-Position-ID die Anzahl & Titel der Plausibilitätsbefunde, die ihr
   * im Plausi-Block zugeordnet sind. Liegt ein Eintrag mit `count > 0` vor,
   * zeigt die jeweilige Zeile ein dezentes Warn-Symbol mit Tooltip; ein Klick
   * springt direkt in den Plausi-Block (siehe `onJumpToPlausibility`).
   */
  plausibilityByPosId?: Record<string, { count: number; titles: string[] }>;
  /** Wird gerufen, wenn der Nutzer das Warn-Symbol einer Zeile anklickt. */
  onJumpToPlausibility?: (posId: string) => void;
  /**
   * Explizites Signal vom Parent, dass es sich um ein BESTANDSANGEBOT handelt
   * (Server hat lvState geliefert ODER LocalStorage hat persistente Daten).
   * Wenn `true`, dürfen STAMMDATEN-Defaults NICHT angewendet werden — auch
   * dann nicht, wenn `initialMengen`/`initialEpOverrides`/`initialCustomPositionen`/
   * `injectedExtras` allesamt leer sind (z.B. Bestandsangebot, in dem alle
   * Default-Pauschalen explizit deaktiviert wurden).
   * Default `false` (rückwärts-kompatibel: Inferenz aus Snapshot-Daten greift).
   */
  isExistingOffer?: boolean;
  /**
   * Optional Whitelist sichtbarer Sektions-IDs. Wenn gesetzt, werden im UI
   * nur die Sektionen aus dieser Liste gerendert (alle anderen sind
   * komplett ausgeblendet, inkl. „Eigene Position hinzufügen"-Formular).
   * State, Mengen-Aggregation, Summen-Berechnung und alle `onChange`-
   * Callbacks bleiben unverändert über ALLE Sektionen — die Filterung
   * betrifft ausschließlich die Anzeige.
   *
   * Default: `undefined` → alle Sektionen sichtbar (rückwärts-kompatibel
   * für andere Aufrufer).
   *
   * Wird in Schritt 4 (`project-edit.tsx`) auf `['s7']` gesetzt, damit
   * der Kalkulator dort nur noch die Pauschalleistungen zur Bearbeitung
   * anzeigt — die übrigen Positionen kommen über die Foto-Übernahme aus
   * Schritt 3 und fließen unverändert in PDF und Gesamtsumme ein.
   */
  visibleSektionIds?: string[];
}

export const STAMMDATEN: LVSektion[] = [
  {
    id: "s1", icon: "⚒", titel: "Betonvorbereitung – Abtragen & Schneiden",
    positionen: [
      { id:"p1",  name:"Beton abtragen vertikal, bewehrt (bis 3,5 cm)", unit:"m²",   ep:119,   qty:0, aktiv:false },
      { id:"p2",  name:"Betonabtrag Untersicht, bewehrt (bis 3,5 cm)",  unit:"m²",   ep:119,   qty:0, aktiv:false },
      { id:"p3",  name:"Mehrtiefe Betonabtrag vertikal (Zuschlag)",      unit:"m²",   ep:119,   qty:0, aktiv:false, tag:"AZ" },
      { id:"p4",  name:"Mehrtiefe Betonabtrag Untersicht (Zuschlag)",    unit:"m²",   ep:119,   qty:0, aktiv:false, tag:"AZ" },
      { id:"p5",  name:"Schnitte Beton 1 cm, vertikale Flächen",        unit:"lfm",  ep:8,     qty:0, aktiv:false },
      { id:"p6",  name:"Schnitte Beton 1 cm, Untersichten",             unit:"lfm",  ep:8,     qty:0, aktiv:false },
      { id:"p7",  name:"AZ Erschwernis Kleinflächen",                   unit:"Stk",  ep:40,    qty:0, aktiv:false, tag:"AZ" },
    ],
  },
  {
    id: "s2", icon: "💧", titel: "HDW-Strahlen (Hochdruckwasser)",
    positionen: [
      { id:"p8",  name:"Beton vertikal HDW-Strahlen, Abreißfest",       unit:"m²",   ep:18,    qty:0, aktiv:false },
      { id:"p9",  name:"Beton Untersicht HDW-Strahlen, Abreißfest",     unit:"m²",   ep:18,    qty:0, aktiv:false },
      { id:"p10", name:"AZ Freilegen Korngerüst 3 mm",                  unit:"m²",   ep:12,    qty:0, aktiv:false, tag:"ALT" },
    ],
  },
  {
    id: "s3", icon: "🔩", titel: "Bewehrung & Verankerung",
    positionen: [
      { id:"p11", name:"Reinigen Bewehrung SA 2,5",                     unit:"lfm",  ep:13,    qty:0, aktiv:false },
      { id:"p12", name:"Ergänzen bestehende Bewehrung, Stabstahl",      unit:"kg",   ep:8.50,  qty:0, aktiv:false },
      { id:"p13", name:"Bohrlöcher Steckeisen horiz. Kümö. 20 mm",      unit:"lfm",  ep:30,    qty:0, aktiv:false },
      { id:"p14", name:"Bohrlöcher Steckeisen vertikal Kümö. 20 mm",    unit:"lfm",  ep:30,    qty:0, aktiv:false },
    ],
  },
  {
    id: "s4", icon: "🧱", titel: "Haftbrücke & Mörtelauftrag",
    positionen: [
      { id:"p15", name:"Haftbrücke hydraulisch vertikal",               unit:"m²",   ep:8.40,  qty:0, aktiv:false },
      { id:"p16", name:"Haftbrücke hydraulisch Untersicht",             unit:"m²",   ep:8.40,  qty:0, aktiv:false },
      { id:"p17", name:"Fertigmörtel R4 XF4 KLFL vertikal, 1,5 cm",    unit:"m²",   ep:93,    qty:0, aktiv:false },
      { id:"p18", name:"Fertigmörtel R4 XF4 KLFL vertikal, Mehrdicke", unit:"m²",   ep:53,    qty:0, aktiv:false, tag:"AZ" },
      { id:"p19", name:"Fertigmörtel R4 XF4 KLFL Untersicht, 1,5 cm",  unit:"m²",   ep:128,   qty:0, aktiv:false },
      { id:"p20", name:"Fertigmörtel R4 XF4 KLFL Untersicht, Mehrdicke",unit:"m²",  ep:75,    qty:0, aktiv:false, tag:"AZ" },
      { id:"p21", name:"AZ Betonkanten Mörtel",                         unit:"lfm",  ep:13,    qty:0, aktiv:false, tag:"AZ" },
    ],
  },
  {
    id: "s5", icon: "🔧", titel: "Risssanierung & Verpressen",
    positionen: [
      { id:"p22", name:"Verpresspacker liefern und versetzen",          unit:"Stk",  ep:12,    qty:0, aktiv:false },
      { id:"p23", name:"Risse abdichten Spachtelmasse",                 unit:"lfm",  ep:15,    qty:0, aktiv:false },
      { id:"p24", name:"Einpressmaterial Zement kraftschlüssig",        unit:"Liter",ep:25,    qty:0, aktiv:false },
      { id:"p25", name:"Risse + Hohlräume verpressen",                  unit:"Liter",ep:35,    qty:0, aktiv:false },
    ],
  },
  {
    id: "s6", icon: "🎨", titel: "Beschichtung (Reinacrylat / Schutzanstrich)",
    positionen: [
      { id:"p26", name:"Untergrund reinigen + Vorbehandlung",           unit:"m²",   ep:6,     qty:0, aktiv:false },
      { id:"p27", name:"Reinacrylat-Beschichtung 1-lagig",              unit:"m²",   ep:14,    qty:0, aktiv:false },
      { id:"p28", name:"Reinacrylat-Beschichtung 2-lagig",              unit:"m²",   ep:22,    qty:0, aktiv:false },
      { id:"p29", name:"Fassadenbeschichtung mineralisch (Silikat)",    unit:"m²",   ep:18,    qty:0, aktiv:false },
      { id:"p30", name:"Schutzbeschichtung Sockelbereich, diffusionsoffen", unit:"m²", ep:28,  qty:0, aktiv:false },
      { id:"p31", name:"Schutzbeschichtung Beton EP-basiert (Industrie)", unit:"m²", ep:45,    qty:0, aktiv:false },
    ],
  },
  {
    id: "s7", icon: "🚚", titel: "Pauschalleistungen",
    positionen: [
      // Pauschalen sind seit Task #93 NICHT mehr per Default vorausgewählt —
      // sie verhalten sich exakt wie alle anderen STAMMDATEN-Positionen
      // (qty=0, aktiv=false). Werte werden ausschließlich über `initialMengen`
      // / Bibliothek aktiviert. Damit bleibt die Auswahl zwischen Speichern
      // und Reload exakt das, was der Nutzer angehakt hat.
      { id:"p32", name:"An- und Abfahrtspauschale",               unit:"Psch", ep:3000, qty:0, aktiv:false },
      { id:"p33", name:"Baustellen-Einrichtung (Gerüst, Schutz)", unit:"Psch", ep:2500, qty:0, aktiv:false },
      { id:"p34", name:"Baustellenreinigung & Entsorgung",        unit:"Psch", ep:800,  qty:0, aktiv:false },
    ],
  },
  {
    id: "s8", icon: "📋", titel: "Leistungspositionen",
    positionen: [],
  },
];

const STANDARD_EINHEITEN = ["m", "m²", "lfm", "Stk", "kg", "Liter", "Psch", "Std"] as const;

function UnitCell({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (val: string) => void;
  testId: string;
}) {
  const isStandard = (STANDARD_EINHEITEN as readonly string[]).includes(value);
  const [customMode, setCustomMode] = useState(!isStandard && value !== "");

  useEffect(() => {
    if ((STANDARD_EINHEITEN as readonly string[]).includes(value)) {
      setCustomMode(false);
    } else if (value !== "") {
      setCustomMode(true);
    }
  }, [value]);

  if (customMode) {
    return (
      <div className="flex items-center gap-0.5">
        <input
          type="text"
          value={value}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          placeholder="Einheit"
          className="w-full text-right text-xs py-1 px-1.5 rounded border border-emerald-400 bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
          data-testid={`${testId}-custom`}
        />
        <button
          type="button"
          onClick={() => {
            setCustomMode(false);
            onChange("Stk");
          }}
          title="Standard-Einheit wählen"
          className="text-[10px] text-muted-foreground hover:text-foreground px-1"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === "__custom__") {
          setCustomMode(true);
          onChange("");
        } else {
          onChange(e.target.value);
        }
      }}
      className="w-full text-right text-xs py-1 px-1 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
      data-testid={testId}
    >
      {STANDARD_EINHEITEN.map((u) => (
        <option key={u} value={u}>
          {u}
        </option>
      ))}
      <option value="__custom__">Andere…</option>
    </select>
  );
}

function VarianteHinzufuegenButton({
  originalUnit,
  currentUnit,
  onDuplicate,
  testId,
}: {
  originalUnit: string;
  currentUnit: string;
  onDuplicate: (originalUnit: string, newUnit: string) => void;
  testId: string;
}) {
  if (!currentUnit || currentUnit === originalUnit) return null;
  return (
    <div className="px-3 pb-2">
      <button
        type="button"
        onClick={() => onDuplicate(originalUnit, currentUnit)}
        className="inline-flex items-center gap-1.5 text-[11px] py-1 px-2 rounded border border-dashed border-amber-400 bg-amber-50/60 text-amber-800 hover:bg-amber-100 hover:border-amber-500 dark:bg-amber-950/20 dark:text-amber-300 transition-colors"
        title="Original behalten und neue Einheit als Variante anlegen (zählt nicht zur Summe)"
        data-testid={testId}
      >
        <CopyPlus className="w-3 h-3" />
        Als Variante mit „{currentUnit}" anlegen (ohne Aufsummierung)
      </button>
    </div>
  );
}

function tiefKopie<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function berechneSummen(sektionen: LVSektion[]): LVSummen {
  let netto = 0;
  sektionen.forEach((s) =>
    s.positionen.forEach((p) => {
      if (p.aktiv && !p.variant) netto += p.qty * p.ep;
    })
  );
  return { netto, mwst: netto * 0.2, brutto: netto * 1.2 };
}

export default function LeistungsverzeichnisKalkulator({
  onPositionenChange,
  initialMengen = {},
  initialMengenRefs = {},
  autoFilledMengen = {},
  onMengeManuallyChanged,
  availableMengen = [],
  mengenBreakdown = {},
  injectedExtras = [],
  initialEpOverrides = {},
  initialCustomPositionen = [],
  onCustomPositionenChange,
  initialManualEpIds = [],
  onManualEpIdsChange,
  currentProjectId,
  onMengenRefsChange,
  highlightPositionId,
  onHighlightConsumed,
  onGoToMeasurementPhoto,
  plausibilityByPosId = {},
  onJumpToPlausibility,
  isExistingOffer: isExistingOfferProp = false,
  visibleSektionIds,
}: LVKalkulatorProps) {
  useRenderCounter("LeistungsverzeichnisKalkulator");
  const [sektionen, setSektionen] = useState<LVSektion[]>(() => {
    const basis = tiefKopie(STAMMDATEN);
    // Bestandsangebot? Wenn Mengen / EP-Overrides / Custom-Positionen / Extras
    // vorliegen, hat der Nutzer bereits gespeichert.
    //
    // Defense-in-Depth seit Task #93: STAMMDATEN enthält keine Default-aktiven
    // Positionen mehr (p32/p34 sind seit Task #93 ebenfalls aktiv:false). Die
    // folgende Reset-Schleife ist heute also faktisch ein no-op und bleibt nur
    // erhalten, damit ein versehentlich neu eingeführter `aktiv:true`-Default
    // bei Bestandsangeboten nicht ungewollt wiederbelebt würde.
    const isExistingOffer =
      isExistingOfferProp ||
      Object.keys(initialMengen).length > 0 ||
      Object.keys(initialEpOverrides).length > 0 ||
      initialCustomPositionen.length > 0 ||
      injectedExtras.length > 0;
    if (isExistingOffer) {
      basis.forEach((s) =>
        s.positionen.forEach((p) => {
          if (p.aktiv) {
            p.aktiv = false;
            p.qty = 0;
          }
        }),
      );
    }
    Object.entries(initialMengen).forEach(([id, menge]) => {
      basis.forEach((s) =>
        s.positionen.forEach((p) => {
          if (p.id === id) {
            p.qty = menge;
            p.aktiv = menge > 0;
          }
        })
      );
    });
    Object.entries(initialEpOverrides).forEach(([id, ep]) => {
      if (!isFinite(ep) || ep <= 0) return;
      basis.forEach((s) =>
        s.positionen.forEach((p) => {
          if (p.id === id) p.ep = ep;
        })
      );
    });
    // Per-Foto-Einheits-Varianten als zusätzliche Zeilen unter der Stamm-Position
    // injizieren. Composite-ID `${baseId}::${unit}` damit der Foto-Breakdown
    // (mengenBreakdown) eindeutig zuordenbar bleibt.
    injectedExtras.forEach((extra) => {
      basis.forEach((s) => {
        const idx = s.positionen.findIndex((p) => p.id === extra.baseId);
        if (idx === -1) return;
        const base = s.positionen[idx];
        const newId = `${extra.baseId}::${extra.unit}`;
        if (s.positionen.some((p) => p.id === newId)) return;
        const klon: LVPosition = {
          id: newId,
          name: `${base.name} (${extra.unit})`,
          unit: extra.unit,
          ep: extra.ep,
          qty: extra.qty,
          aktiv: extra.qty > 0,
          tag: base.tag,
        };
        s.positionen.splice(idx + 1, 0, klon);
      });
    });
    // Persistierte eigene Positionen pro Sektion zurückspielen.
    initialCustomPositionen.forEach((cp) => {
      const sek = basis.find((s) => s.id === cp.sektionId);
      if (!sek) return;
      if (sek.positionen.some((p) => p.id === cp.id)) return;
      sek.positionen.push({
        id: cp.id,
        name: cp.name,
        unit: cp.unit,
        ep: cp.ep,
        qty: cp.qty,
        aktiv: cp.aktiv,
        tag: cp.tag ?? undefined,
        beschreibung: cp.beschreibung ?? null,
      });
    });
    return basis;
  });

  const [offeneSektionen, setOffeneSektionen] = useState<Set<string>>(
    new Set()
  );

  // Pro Position-ID: vom Nutzer manuell zugeklappte Beschreibungs-Panels.
  // Default: Panel ist offen, wenn `beschreibung` Inhalt hat. Sobald der
  // Nutzer es zuklappt, merken wir uns das hier, damit Re-Renders die
  // Wahl nicht überschreiben (sonst würde `<details open>` jedes Mal wieder
  // aufspringen).
  const [collapsedBeschreibung, setCollapsedBeschreibung] = useState<Set<string>>(new Set());

  const [mengenRefs, setMengenRefs] = useState<Record<string, MengenRef>>(() => initialMengenRefs);
  const [showPicker, setShowPicker] = useState<string | null>(null);
  const originalUnitsRef = useRef<Record<string, string>>(
    (() => {
      const map: Record<string, string> = {};
      STAMMDATEN.forEach((s) => s.positionen.forEach((p) => { map[p.id] = p.unit; }));
      return map;
    })()
  );
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [manualEpIds, setManualEpIds] = useState<Set<string>>(() => new Set(initialManualEpIds));
  const lastManualEpHashRef = useRef<string | null>(null);

  // Re-hydrate manualEpIds when the parent updates `initialManualEpIds`
  // (server-first hydration arrives after mount).
  useEffect(() => {
    setManualEpIds((prev) => {
      const incoming = new Set(initialManualEpIds);
      if (prev.size === incoming.size && Array.from(prev).every((id) => incoming.has(id))) {
        return prev;
      }
      return incoming;
    });
  }, [JSON.stringify(initialManualEpIds)]);

  // Re-hydrate EP-Overrides aus dem Parent. Wird ausgelöst, wenn der Parent
  // einen neuen/geänderten Override liefert (z.B. nach Inline-EP-Edit aus der
  // Plausibilitätsprüfung). Wir wenden nur Werte an, die sich tatsächlich
  // unterscheiden, und markieren die betroffenen IDs als „manuell" — sonst
  // würde der LV beim nächsten Mengen-Repricing den alten Bibliothekspreis
  // wiederziehen.
  useEffect(() => {
    const entries = Object.entries(initialEpOverrides);
    if (entries.length === 0) return;
    setSektionen((prev) => {
      let changed = false;
      const next = prev.map((s) => ({
        ...s,
        positionen: s.positionen.map((p) => {
          const override = initialEpOverrides[p.id];
          // Auch 0 zulassen — Nutzer kann bewusst „kein EP" speichern.
          if (
            typeof override === "number" &&
            isFinite(override) &&
            override >= 0 &&
            override !== p.ep
          ) {
            changed = true;
            return { ...p, ep: override };
          }
          return p;
        }),
      }));
      return changed ? next : prev;
    });
    setManualEpIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const [id] of entries) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // JSON-Stringify als Dep verhindert Loops durch neue Object-Identitäten
    // ohne semantische Änderung.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialEpOverrides)]);

  // Manual-EP-IDs an Parent emittieren (zum Persistieren in lvState).
  useEffect(() => {
    if (!onManualEpIdsChange) return;
    const ids = Array.from(manualEpIds).sort();
    const h = JSON.stringify(ids);
    if (lastManualEpHashRef.current === h) return;
    lastManualEpHashRef.current = h;
    onManualEpIdsChange(ids);
  }, [manualEpIds, onManualEpIdsChange]);

  const { toast } = useToast();
  const { data: libraryPositions = [] } = useQuery<LibraryPosition[]>({
    queryKey: ["/api/library-positions"],
  });

  // Feedback-Loop: sobald eine Library-Zeile im LV aktiv ist und eine Menge
  // gesetzt hat, wird ein Preis-Sample (menge, ep) zurück in die
  // Wissensdatenbank geschrieben. Dedup via Session-Ref, damit identische
  // Kombinationen nicht mehrfach POSTen.
  const postedSamplesRef = useRef<Set<string>>(new Set());
  const projectIdForSamples = currentProjectId ?? null;
  useEffect(() => {
    for (const s of sektionen) {
      for (const p of s.positionen) {
        if (!p.library || !p.aktiv) continue;
        if (!(p.qty > 0) || !(p.ep > 0)) continue;
        const libIdStr = p.id.replace(/^lib-/, "");
        const libId = Number(libIdStr);
        if (!Number.isInteger(libId) || libId <= 0) continue;
        const key = `${libId}:${p.qty.toFixed(4)}:${p.ep.toFixed(2)}`;
        if (postedSamplesRef.current.has(key)) continue;
        postedSamplesRef.current.add(key);
        apiRequest("POST", `/api/library-positions/${libId}/price-sample`, {
          menge: p.qty,
          ep: p.ep,
          quelle: "projekt",
          projektId: projectIdForSamples,
        })
          .then(() => queryClient.invalidateQueries({ queryKey: ["/api/library-positions"] }))
          .catch(() => {
            // Stille-Fehler: wenn Server ablehnt (z. B. Legacy-Global),
            // nichts weiter tun.
          });
      }
    }
  }, [sektionen, projectIdForSamples]);

  // Merge library positions into the matching sektionen as additional rows.
  // ID-based upsert keyed by `lib-<libId>` with two distinct row classes:
  //
  //  1) PERSISTED rows  — rows whose id appears in `initialMengen`, i.e. the
  //     project already has saved data for them. These represent a frozen
  //     snapshot owned by the project. They are NEVER mutated by library
  //     changes (no metadata refresh, no deactivation, no removal). This
  //     guarantees: "Löschen oder Deaktivieren einer Bibliothek-Position
  //     beeinflusst keine bereits in Projekten gespeicherten Werte."
  //
  //  2) EPHEMERAL rows — rows that were merged in only because the library
  //     currently exposes them in this open session and no project value is
  //     persisted for them yet. These are kept in sync with the library:
  //     metadata (name/unit/ep/tag) is refreshed live, and the row is removed
  //     when the library entry is deactivated or deleted.
  //
  //  New library entries are appended at the end of the matching sektion.
  useEffect(() => {
    setSektionen((prev) => {
      const libById = new Map<string, LibraryPosition>();
      for (const lp of libraryPositions) libById.set(`lib-${lp.id}`, lp);

      let changed = false;
      const next = prev.map((s) => {
        const seenLibIds = new Set<string>();
        const updated: LVPosition[] = [];

        for (const p of s.positionen) {
          if (!p.id.startsWith("lib-")) {
            updated.push(p);
            continue;
          }

          const isPersisted = Object.prototype.hasOwnProperty.call(initialMengen, p.id);
          const lp = libById.get(p.id);
          const libActiveHere = !!lp && lp.sektionId === s.id && lp.aktiv;
          if (libActiveHere && lp) {
            seenLibIds.add(p.id);
            // Bibliothek ist Single Source of Truth: name/unit/tag werden
            // immer aus der Bibliothek aktualisiert (auch für persistierte
            // Zeilen). EP wird mengen-abhängig nachgezogen, sofern der
            // Nutzer ihn nicht ausdrücklich überschrieben hat.
            const pricedEp = manualEpIds.has(p.id) ? p.ep : pickLibraryEp(lp, p.qty);
            const refreshed: LVPosition = {
              ...p,
              name: lp.name,
              unit: lp.unit,
              ep: pricedEp,
              tag: lp.tag ?? undefined,
              library: true,
            };
            if (
              refreshed.name !== p.name ||
              refreshed.unit !== p.unit ||
              refreshed.ep !== p.ep ||
              refreshed.tag !== p.tag ||
              refreshed.library !== p.library
            ) {
              changed = true;
            }
            updated.push(refreshed);
          } else if (isPersisted) {
            // Library-Eintrag wurde deaktiviert/gelöscht, aber das Projekt
            // hat den Preis/Stand snapshot-mäßig gespeichert → erhalten.
            updated.push(p);
            seenLibIds.add(p.id);
          } else {
            // Ephemerer Eintrag ohne persistenten Stand und ohne aktive
            // Library-Quelle → fallen lassen.
            changed = true;
          }
        }

        for (const lp of libraryPositions) {
          if (lp.sektionId !== s.id || !lp.aktiv) continue;
          const id = `lib-${lp.id}`;
          if (seenLibIds.has(id)) continue;
          const hasPersistedQty = Object.prototype.hasOwnProperty.call(initialMengen, id);
          const hasPersistedEp = Object.prototype.hasOwnProperty.call(initialEpOverrides, id);
          // Bei Bestandsangeboten: Library-Einträge NICHT automatisch wieder
          // anhängen, wenn das Projekt sie noch nie verwendet hat. Sonst
          // tauchen ALLE freigegebenen Bibliotheks-Positionen nach jedem
          // Reload als „qty=0"-Zeilen wieder im LV auf — wäre Spam, gerade
          // bei wachsender Bibliothek. Universelle Schutzlogik, nicht nur
          // pauschalspezifisch. Detection identisch zu useState-Init und
          // Re-Hydration-Effect: injectedExtras zählen mit, damit ein
          // Bestand, der nur via Foto-Extras erkennbar wäre, nicht
          // fälschlich als „neu" gilt.
          const isExistingOffer =
            isExistingOfferProp ||
            Object.keys(initialMengen).length > 0 ||
            Object.keys(initialEpOverrides).length > 0 ||
            initialCustomPositionen.length > 0 ||
            injectedExtras.length > 0;
          if (isExistingOffer && !hasPersistedQty && !hasPersistedEp) continue;
          const persistedQty = initialMengen[id] ?? 0;
          updated.push({
            id,
            name: lp.name,
            unit: lp.unit,
            // Auch bei neu in den LV aufgenommenen Library-Zeilen mengen-
            // abhängigen EP verwenden, sofern Preis-Samples vorliegen.
            ep: pickLibraryEp(lp, persistedQty),
            qty: persistedQty,
            aktiv: hasPersistedQty ? persistedQty > 0 : false,
            tag: lp.tag ?? undefined,
            library: true,
          });
          changed = true;
        }

        return { ...s, positionen: updated };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryPositions, manualEpIds]);

  useEffect(() => {
    if (!highlightPositionId) return;

    const sectionWithPos = sektionen.find((s) =>
      s.positionen.some((p) => p.id === highlightPositionId)
    );
    if (sectionWithPos) {
      setOffeneSektionen((prev) => {
        const neu = new Set(prev);
        neu.add(sectionWithPos.id);
        return neu;
      });
    }

    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    setHighlightedId(highlightPositionId);

    const scrollTimer = setTimeout(() => {
      const el = document.querySelector(`[data-lv-pos-id="${highlightPositionId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      highlightTimerRef.current = setTimeout(() => {
        setHighlightedId(null);
        if (onHighlightConsumed) onHighlightConsumed();
      }, 2000);
    }, 80);

    return () => {
      clearTimeout(scrollTimer);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [highlightPositionId]);

  useEffect(() => {
    // Re-Hydration nach Server-First-Load: feuert ausschließlich bei Änderung
    // der `initialMengen`-Snapshot (Mount-Hydration oder Foto-Auto-Apply via
    // `lvApplyVersion`-Remount). NICHT bei in-session EP-Edits oder Custom-
    // Positions-Änderungen, da der Eltern-Save-Pfad die EP-/Custom-State sonst
    // mit stale `initialMengen` überschreiben würde (Regression).
    if (Object.keys(initialMengen).length === 0) return;
    setSektionen((prev) => {
      const neu = tiefKopie(prev);
      // Spiegel des useState-Initializers: für jede STAMMDATEN-Default-aktive
      // Position, die NICHT explizit in initialMengen steht, qty/aktiv auf 0
      // setzen.
      //
      // Defense-in-Depth seit Task #93: Aktuell sind keine STAMMDATEN-Defaults
      // mehr `aktiv:true`, also bleibt `stammDefaultActive` leer und der Reset
      // ist ein no-op. Bleibt erhalten, falls künftig wieder ein Default-aktives
      // Feld eingeführt würde.
      const explicitMengenIds = new Set(Object.keys(initialMengen));
      const stammDefaultActive = new Set<string>();
      STAMMDATEN.forEach((s) =>
        s.positionen.forEach((p) => {
          if (p.aktiv) stammDefaultActive.add(p.id);
        }),
      );
      neu.forEach((s) =>
        s.positionen.forEach((p, idx) => {
          if (stammDefaultActive.has(p.id) && !explicitMengenIds.has(p.id)) {
            s.positionen[idx] = { ...p, qty: 0, aktiv: false };
          }
        }),
      );
      // Race-Cleanup: Library-Auto-Append kann VOR dieser Hydration gefeuert
      // haben (warmer libraryPositions-Cache → der Library-Merge-useEffect
      // reagiert nicht auf initialMengen-Änderungen). Verbliebene `lib-*`-
      // Ghost-Zeilen ohne persistierte Spur jetzt entfernen, sonst tauchen
      // sie nach Reload eines Bestandsangebots als 0-Mengen-Duplikate auf.
      // Vom Nutzer aktivierte Lib-Zeilen (qty>0 ODER aktiv) bleiben erhalten.
      neu.forEach((s) => {
        s.positionen = s.positionen.filter((p) => {
          if (!p.id.startsWith("lib-")) return true;
          const hasPersistedQty = Object.prototype.hasOwnProperty.call(initialMengen, p.id);
          const hasPersistedEp = Object.prototype.hasOwnProperty.call(initialEpOverrides, p.id);
          if (hasPersistedQty || hasPersistedEp) return true;
          return p.qty > 0 || p.aktiv;
        });
      });
      Object.entries(initialMengen).forEach(([id, menge]) => {
        neu.forEach((s) =>
          s.positionen.forEach((p, idx) => {
            if (p.id === id) {
              // Library-Zeile + nicht manuell überschrieben → EP zusätzlich
              // mengen-abhängig aus den Preis-Samples neu ziehen, damit
              // persistierte Mengenänderungen nicht mit veraltetem EP landen.
              const repriced = applyQtyWithLibraryRepricing(
                p,
                menge,
                libraryPositions,
                manualEpIds,
              );
              s.positionen[idx] = {
                ...repriced,
                // Sync active flag: qty > 0 activates, qty === 0 explicitly deactivates.
                // Eine persistierte Menge ist die Single Source of Truth für den
                // aktiv-Zustand der Position — egal ob STAMMDATEN oder Bibliothek.
                aktiv: menge > 0,
              };
            }
          })
        );
      });
      // Guard: if the deep copy is content-equal to prev, return prev to keep the
      // reference stable and avoid spurious downstream effects (render loop).
      if (JSON.stringify(prev) === JSON.stringify(neu)) return prev;
      return neu;
    });
  }, [JSON.stringify(initialMengen)]);

  // Belt-and-Suspenders: Wenn Server-Hydration eines Bestandsangebots NUR
  // über `initialEpOverrides` / `initialCustomPositionen` / `injectedExtras`
  // erkennbar ist (initialMengen leer), feuert der primäre Re-Hydration-Effect
  // oben nicht. Dann müssen Library-Ghost-Zeilen trotzdem entfernt werden,
  // die ein vor der Hydration eingetragener Library-Auto-Append (warmer
  // libraryPositions-Cache) als Ghost-Zeile übrig gelassen hätte.
  //
  // Defense-in-Depth seit Task #93: Der zusätzliche STAMMDATEN-Default-Reset
  // bleibt im Code, läuft aber leer — keine Position ist mehr default-aktiv.
  //
  // useRef-Guard: Dieser Reset darf NUR EINMAL pro Mount-Phase laufen, sonst
  // würde ein in-session EP-Edit (welches setLvInitialEpOverrides triggert)
  // potenziell legitime User-Aktivierungen rückgängig machen. Bei
  // Foto-Apply-Remount gibt es ein neues Ref → erneuter Single-Shot.
  //
  // WICHTIG: Wir gaten STRIKT auf `isExistingOfferProp` (vom Parent geliefertes
  // explizites Bestandsangebot-Signal aus `lvLoadState === 'loaded_with_data'`).
  // Snapshot-basierte Inferenz (initialEpOverrides etc. nicht-leer) ist hier
  // VERBOTEN: bei einem neuen Angebot füllen sich diese Snapshots durch
  // in-session User-Edits.
  const hydrationResetDoneRef = useRef(false);
  useEffect(() => {
    if (hydrationResetDoneRef.current) return;
    if (!isExistingOfferProp) return;
    hydrationResetDoneRef.current = true;
    setSektionen((prev) => {
      const explicitMengenIds = new Set(Object.keys(initialMengen));
      const stammDefaultActive = new Set<string>();
      STAMMDATEN.forEach((s) =>
        s.positionen.forEach((p) => {
          if (p.aktiv) stammDefaultActive.add(p.id);
        }),
      );
      let changed = false;
      const next = prev.map((s) => {
        // 1) Defaults-Reset: STAMMDATEN-default-aktive Positionen ohne
        //    expliziten initialMengen-Eintrag deaktivieren.
        // 2) Ghost-Cleanup: lib-* ohne persistierte Spur und im Default-
        //    Ghost-State (qty<=0, !aktiv) entfernen.
        const reset = s.positionen.map((p) => {
          if (stammDefaultActive.has(p.id) && !explicitMengenIds.has(p.id) && p.aktiv) {
            changed = true;
            return { ...p, qty: 0, aktiv: false };
          }
          return p;
        });
        const filtered = reset.filter((p) => {
          if (!p.id.startsWith("lib-")) return true;
          const hasPersistedQty = Object.prototype.hasOwnProperty.call(initialMengen, p.id);
          const hasPersistedEp = Object.prototype.hasOwnProperty.call(initialEpOverrides, p.id);
          if (hasPersistedQty || hasPersistedEp) return true;
          return p.qty > 0 || p.aktiv;
        });
        if (filtered.length !== reset.length) changed = true;
        if (filtered === s.positionen) return s;
        return { ...s, positionen: filtered };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isExistingOfferProp,
    JSON.stringify(initialMengen),
    JSON.stringify(initialEpOverrides),
    JSON.stringify(initialCustomPositionen),
    JSON.stringify(injectedExtras),
  ]);

  // Automatisch berechnete Mengen (z.B. Aufmaß-Gesamtfläche) übernehmen,
  // OHNE das aktiv-Flag zu verändern. Der Nutzer entscheidet selbst per
  // Checkbox, ob die Position ins Angebot einfließt.
  useEffect(() => {
    const ids = Object.keys(autoFilledMengen);
    if (ids.length === 0) return;
    setSektionen((prev) => {
      let changed = false;
      const neu = prev.map((s) => ({
        ...s,
        positionen: s.positionen.map((p) => {
          if (!(p.id in autoFilledMengen)) return p;
          const target = autoFilledMengen[p.id];
          if (Math.abs((p.qty ?? 0) - target) < 0.0001) return p;
          changed = true;
          // Library-Zeile + nicht manuell überschrieben → EP zusätzlich
          // mengen-abhängig aus den Preis-Samples neu ziehen, sonst
          // nur die Menge übernehmen.
          return applyQtyWithLibraryRepricing(p, target, libraryPositions, manualEpIds);
        }),
      }));
      return changed ? neu : prev;
    });
  }, [JSON.stringify(autoFilledMengen)]);

  useEffect(() => {
    // No early-return: allow parent to clear refs by passing empty object {}
    // Guard: only set a new reference when the content actually differs,
    // so downstream effects don't fire on identical input.
    setMengenRefs((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(initialMengenRefs)) return prev;
      return initialMengenRefs;
    });
  }, [JSON.stringify(initialMengenRefs)]);

  // Inhalts-Hash-Gate gegen React #185:
  // L241/L267/L286 setzen `sektionen`/`mengenRefs` beim Mount nacheinander.
  // Selbst mit den Equality-Guards in den Settern bleiben Fälle, in denen die
  // Referenz wechselt, der Inhalt aber identisch zur letzten Emission ist
  // (z.B. nach Parent-Re-Render durch QuoteGenerator-onQuoteChange).
  // Ohne Gate würden `setLvPositionen/setLvSummen/setLvMengenRefs` in der Eltern-
  // Komponente jedes Mal neue Array/Object-Referenzen schreiben → QuoteGenerator
  // re-rendert → Loop. Wir spiegeln das `lastEmittedHashRef`-Muster aus
  // QuoteGenerator und emitten nur, wenn der Inhalt sich tatsächlich geändert hat.
  // Eigene Positionen, die erst NACH dem Mount via Server-Hydration eintreffen,
  // nachträglich in die passenden Sektionen mergen (idempotent).
  useEffect(() => {
    if (initialCustomPositionen.length === 0) return;
    setSektionen((prev) => {
      const existingIds = new Set<string>();
      prev.forEach((s) => s.positionen.forEach((p) => existingIds.add(p.id)));
      const toAdd = initialCustomPositionen.filter((cp) => !existingIds.has(cp.id));
      if (toAdd.length === 0) return prev;
      return prev.map((s) => {
        const add = toAdd.filter((cp) => cp.sektionId === s.id);
        if (add.length === 0) return s;
        return {
          ...s,
          positionen: [
            ...s.positionen,
            ...add.map((cp) => ({
              id: cp.id,
              name: cp.name,
              unit: cp.unit,
              ep: cp.ep,
              qty: cp.qty,
              aktiv: cp.aktiv,
              tag: cp.tag ?? undefined,
              beschreibung: cp.beschreibung ?? null,
            })),
          ],
        };
      });
    });
  }, [JSON.stringify(initialCustomPositionen)]);

  // Eigene Positionen (custom-…) als komplette Liste an den Parent emittieren —
  // inkl. deaktivierter Zeilen, damit der Parent die Stammdaten persistieren
  // kann (Bezeichnung/Einheit/EP/Menge bleiben nach Reload erhalten).
  const lastCustomHashRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onCustomPositionenChange) return;
    const cps: ProjectCustomPosition[] = sektionen.flatMap((s) =>
      s.positionen
        .filter((p) => p.id.startsWith("custom-"))
        .map((p) => ({
          id: p.id,
          sektionId: s.id,
          name: p.name,
          unit: p.unit,
          ep: p.ep,
          qty: p.qty,
          aktiv: p.aktiv,
          tag: p.tag ?? null,
          beschreibung: p.beschreibung ?? null,
        }))
    );
    const h = JSON.stringify(cps);
    if (lastCustomHashRef.current === h) return;
    lastCustomHashRef.current = h;
    onCustomPositionenChange(cps);
  }, [sektionen]);

  const lastEmittedHashRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onPositionenChange) return;
    // Varianten gehen mit raus, damit sie im Angebot/PDF als Vergleichszeile
    // sichtbar sind. Die Summenbildung (berechneSummen + QuoteGenerator-subtotal)
    // schließt sie weiterhin per `variant`-Flag aus.
    const aktiv = sektionen.flatMap((s) => s.positionen.filter((p) => p.aktiv));
    const summen = berechneSummen(sektionen);
    const contentHash = JSON.stringify({
      aktiv: aktiv.map((p) => ({ id: p.id, qty: p.qty, ep: p.ep, unit: p.unit, name: p.name, variant: !!p.variant, beschreibung: p.beschreibung ?? null })),
      summen,
      mengenRefs,
    });
    if (lastEmittedHashRef.current === contentHash) return;
    lastEmittedHashRef.current = contentHash;
    onPositionenChange(aktiv, summen, mengenRefs);
  }, [sektionen, mengenRefs]);

  const toggleSektion = (sid: string) => {
    setOffeneSektionen((prev) => {
      const neu = new Set(prev);
      neu.has(sid) ? neu.delete(sid) : neu.add(sid);
      return neu;
    });
  };

  const togglePosition = (sid: string, pid: string) => {
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              positionen: s.positionen.map((p) => {
                if (p.id !== pid) return p;
                const willBeActive = !p.aktiv;
                const newQty = willBeActive && p.qty === 0 ? 1 : p.qty;
                // Wenn die Menge sich beim Aktivieren ändert (0 → 1), für
                // Library-Zeilen den EP mengen-abhängig neu ziehen.
                const repriced = applyQtyWithLibraryRepricing(
                  p,
                  newQty,
                  libraryPositions,
                  manualEpIds,
                );
                return { ...repriced, aktiv: willBeActive };
              }),
            }
      )
    );
  };

  const aendereMenge = (sid: string, pid: string, val: string) => {
    const qty = Math.max(0, parseFloat(val) || 0);
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              positionen: s.positionen.map((p) => {
                if (p.id !== pid) return p;
                // Library-Zeile + nicht manuell überschrieben → EP gemäß
                // Mengen-abhängiger Auswahl aus der Wissensdatenbank neu ziehen.
                if (p.library && !manualEpIds.has(p.id) && p.id.startsWith("lib-")) {
                  const libIdNum = Number(p.id.replace(/^lib-/, ""));
                  const lp = libraryPositions.find((l) => l.id === libIdNum);
                  if (lp) {
                    return { ...p, qty, ep: pickLibraryEp(lp, qty) };
                  }
                }
                return { ...p, qty };
              }),
            }
      )
    );
  };

  const aendereEP = (sid: string, pid: string, val: string) => {
    const ep = Math.max(0, parseFloat(val) || 0);
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              positionen: s.positionen.map((p) =>
                p.id !== pid ? p : { ...p, ep }
              ),
            }
      )
    );
    // Sobald der Nutzer den EP einer Position aktiv ändert, gilt sie als
    // "manuell" und wird bei Mengen-Änderungen NICHT mehr aus der Bibliothek
    // überschrieben.
    setManualEpIds((prev) => {
      if (prev.has(pid)) return prev;
      const next = new Set(prev);
      next.add(pid);
      return next;
    });
  };

  const addLibraryPositionById = (libId: number) => {
    const lp = libraryPositions.find((l) => l.id === libId);
    if (!lp) {
      toast({ title: "Bibliotheks-Position nicht gefunden", variant: "destructive" });
      return;
    }
    // Schutz seit Task #109: Wenn der Aufrufer die sichtbaren Sektionen
    // eingeschränkt hat (z.B. Schritt 4 zeigt nur Pauschalleistungen) und
    // das Bibliotheks-Item zu einer ausgeblendeten Sektion gehört, wäre
    // die Zeile nach dem Hinzufügen unsichtbar — verwirrend für den Nutzer.
    // Wir lehnen den Pick deshalb ab und melden den Grund per Toast. Die
    // Picker-UI ist außerdem bereits so gefiltert, dass solche Items in
    // diesem Modus gar nicht erst zur Auswahl stehen — diese Prüfung ist
    // ein Defense-in-Depth.
    if (visibleSektionIds && !visibleSektionIds.includes(lp.sektionId)) {
      toast({
        title: "Position kann hier nicht hinzugefügt werden",
        description: `„${lp.name}" gehört zu einer Sektion, die in diesem Schritt nicht bearbeitet wird.`,
        variant: "destructive",
      });
      return;
    }
    const id = `lib-${libId}`;
    let didAdd = false;
    let alreadyActive = false;
    setSektionen((prev) =>
      prev.map((s) => {
        if (s.id !== lp.sektionId) return s;
        const idx = s.positionen.findIndex((p) => p.id === id);
        if (idx >= 0) {
          const cur = s.positionen[idx];
          if (cur.aktiv && cur.qty > 0) {
            alreadyActive = true;
            return s;
          }
          const updated: LVPosition = {
            ...cur,
            aktiv: true,
            qty: cur.qty > 0 ? cur.qty : 1,
            // EP nur neu ziehen, wenn nicht manuell überschrieben.
            ep: manualEpIds.has(id) ? cur.ep : pickLibraryEp(lp, cur.qty > 0 ? cur.qty : 1),
            library: true,
          };
          const positionen = [...s.positionen];
          positionen[idx] = updated;
          didAdd = true;
          return { ...s, positionen };
        }
        const newPos: LVPosition = {
          id,
          name: lp.name,
          unit: lp.unit,
          ep: pickLibraryEp(lp, 1),
          qty: 1,
          aktiv: true,
          tag: lp.tag ?? undefined,
          library: true,
        };
        didAdd = true;
        return { ...s, positionen: [...s.positionen, newPos] };
      }),
    );
    if (alreadyActive) {
      toast({ title: "Bereits im LV", description: lp.name });
      // Bestehende Zeile fokussieren: Sektion aufklappen + Highlight setzen.
      setOffeneSektionen((prev) => {
        const neu = new Set(prev);
        neu.add(lp.sektionId);
        return neu;
      });
      setHighlightedId(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-lv-pos-id="${id}"]`);
        if (el && "scrollIntoView" in el) {
          (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);
      setTimeout(() => setHighlightedId((cur) => (cur === id ? null : cur)), 2200);
    } else if (didAdd) {
      toast({ title: "Aus Bibliothek hinzugefügt", description: lp.name });
      // Sektion aufklappen, damit die neue Zeile sichtbar ist.
      setOffeneSektionen((prev) => {
        const neu = new Set(prev);
        neu.add(lp.sektionId);
        return neu;
      });
    }
  };

  const aendereBeschreibung = (sid: string, pid: string, val: string) => {
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              positionen: s.positionen.map((p) =>
                p.id !== pid ? p : { ...p, beschreibung: val }
              ),
            }
      )
    );
  };

  const aendereName = (sid: string, pid: string, val: string) => {
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              positionen: s.positionen.map((p) =>
                p.id !== pid ? p : { ...p, name: val }
              ),
            }
      )
    );
  };

  const aendereUnit = (sid: string, pid: string, val: string) => {
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              positionen: s.positionen.map((p) =>
                p.id !== pid ? p : { ...p, unit: val }
              ),
            }
      )
    );
  };

  const positionHinzufuegen = (
    sid: string,
    neuePosInit?: { name?: string; unit?: string; ep?: number; qty?: number; tag?: string | null; saveToLibrary?: boolean }
  ) => {
    const initData = neuePosInit ?? {};
    const name = (initData.name ?? "").trim() || "Eigene Position";
    const unit = (initData.unit ?? "Stk").trim() || "Stk";
    const ep = initData.ep ?? 0;
    const qty = initData.qty ?? 1;
    const tag = initData.tag ?? null;
    const saveToLibrary = initData.saveToLibrary ?? false;
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              positionen: [
                ...s.positionen,
                {
                  id: `custom-${sid}-${
                    typeof crypto !== "undefined" && "randomUUID" in crypto
                      ? crypto.randomUUID()
                      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
                  }`,
                  name,
                  unit,
                  ep,
                  qty,
                  aktiv: true,
                  tag: tag ?? undefined,
                },
              ],
            }
      )
    );
    if (saveToLibrary) {
      apiRequest("POST", "/api/library-positions", {
        sektionId: sid,
        name,
        unit,
        ep,
        tag: tag ?? null,
        aktiv: true,
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/library-positions"] });
          toast({ title: "In Bibliothek gespeichert", description: name });
        })
        .catch((e: Error) => {
          toast({ title: "Bibliothek-Speichern fehlgeschlagen", description: e.message, variant: "destructive" });
        });
    }
  };

  const positionDuplizierenMitEinheit = (sid: string, pid: string, originalUnit: string, newUnit: string) => {
    setSektionen((prev) =>
      prev.map((s) => {
        if (s.id !== sid) return s;
        const idx = s.positionen.findIndex((p) => p.id === pid);
        if (idx === -1) return s;
        const original = s.positionen[idx];
        const klon: LVPosition = {
          ...original,
          id: `dup-${pid}-${Date.now()}`,
          unit: newUnit,
          aktiv: true,
          variant: true,
        };
        const wiederhergestellt: LVPosition = { ...original, unit: originalUnit };
        const neuePositionen = [...s.positionen];
        neuePositionen.splice(idx, 1, wiederhergestellt, klon);
        return { ...s, positionen: neuePositionen };
      })
    );
  };

  const positionEntfernen = (sid: string, pid: string) => {
    setSektionen((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : { ...s, positionen: s.positionen.filter((p) => p.id !== pid) }
      )
    );
    setMengenRefs((prev) => {
      if (!prev[pid]) return prev;
      const next = { ...prev };
      delete next[pid];
      return next;
    });
    // Manuelle EP-Markierung mit-entfernen, sonst würde ein erneutes
    // Hinzufügen derselben Position dauerhaft als "manuell" gelten und
    // die mengenabhängige Bibliotheks-EP-Auswahl blockieren.
    setManualEpIds((prev) => {
      if (!prev.has(pid)) return prev;
      const next = new Set(prev);
      next.delete(pid);
      return next;
    });
  };

  const allesZuruecksetzen = () => {
    const basis = tiefKopie(STAMMDATEN);
    Object.entries(initialMengen).forEach(([id, menge]) => {
      basis.forEach((s) =>
        s.positionen.forEach((p) => {
          if (p.id === id) { p.qty = menge; p.aktiv = menge > 0; }
        })
      );
    });
    setSektionen(basis);
    setMengenRefs({});
  };

  const summen = berechneSummen(sektionen);

  // Prune: manuelle EP-Markierungen, die zu keiner aktuell existierenden
  // Position mehr passen, ausräumen — verhindert "stale" Manual-Flags, die
  // sonst nach Re-Add derselben ID die Mengen-Repricing-Logik blockieren.
  useEffect(() => {
    if (manualEpIds.size === 0) return;
    const existing = new Set<string>();
    for (const s of sektionen) for (const p of s.positionen) existing.add(p.id);
    const ids = Array.from(manualEpIds);
    const needsPrune = ids.some((id) => !existing.has(id));
    if (!needsPrune) return;
    setManualEpIds((prev) => {
      const next = new Set<string>();
      Array.from(prev).forEach((id) => {
        if (existing.has(id)) next.add(id);
      });
      return next;
    });
  }, [sektionen, manualEpIds]);

  const activeLibIds = useMemo(() => {
    const set = new Set<number>();
    for (const s of sektionen) {
      for (const p of s.positionen) {
        if (!p.id.startsWith("lib-")) continue;
        if (!p.aktiv || !(p.qty > 0)) continue;
        const n = Number(p.id.replace(/^lib-/, ""));
        if (Number.isInteger(n)) set.add(n);
      }
    }
    return set;
  }, [sektionen]);

  // UI-Filter: nur die per `visibleSektionIds` whitelisteten Sektionen
  // anzeigen. State, Mengen, Summen-Berechnung, `onPositionenChange` etc.
  // arbeiten weiterhin mit dem vollständigen `sektionen`-Array — die
  // Filterung greift ausschließlich für die Anzeige.
  const sichtbareSektionen = visibleSektionIds
    ? sektionen.filter((s) => visibleSektionIds.includes(s.id))
    : sektionen;

  return (
    <div className="w-full space-y-4">
      {sichtbareSektionen.map((sek) => {
        const sekSumme = sek.positionen
          .filter((p) => p.aktiv)
          .reduce((acc, p) => acc + p.qty * p.ep, 0);
        const istOffen = offeneSektionen.has(sek.id);

        return (
          <div key={sek.id} className="border border-border rounded-xl overflow-hidden bg-card">
            <button
              type="button"
              onClick={() => toggleSektion(sek.id)}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg w-7 text-center">{sek.icon}</span>
                <span className="text-sm font-medium">{sek.titel}</span>
              </div>
              <div className="flex items-center gap-2">
                {sekSumme > 0 && (
                  <span className="text-xs text-emerald-600 font-medium tabular-nums">
                    {"\u20AC"} {euFormat(sekSumme)}
                  </span>
                )}
                <span className="text-muted-foreground text-xs">
                  {istOffen ? "\u25B2" : "\u25BC"}
                </span>
              </div>
            </button>

            {istOffen && (
              <div className="border-t border-border">
                <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_4.5rem_3.5rem_4.5rem_4.5rem_2rem] gap-1 px-3 py-1.5 bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <span />
                  <span>Leistung</span>
                  <span className="text-right">Menge</span>
                  <span className="text-right">Einheit</span>
                  <span className="text-right">EP {"\u20AC"}</span>
                  <span className="text-right">Gesamt</span>
                  <span />
                </div>

                {sek.positionen.map((pos) => {
                  const gp = pos.aktiv ? pos.qty * pos.ep : 0;
                  const ref = mengenRefs[pos.id];
                  const pickerOpen = showPicker === pos.id;
                  const breakdown = mengenBreakdown[pos.id] ?? [];
                  const breakdownSum = breakdown.reduce((s, e) => s + e.qty, 0);
                  const isOverridden = breakdown.length > 0 && Math.abs(breakdownSum - pos.qty) > 0.005;
                  const originalUnit = originalUnitsRef.current[pos.id] ?? pos.unit;
                  // Library-Quellinfo (mengenabhängige Preis-Auswahl) für Anzeige.
                  let libInfo: ReturnType<typeof pickLibraryEpInfo> | null = null;
                  if (pos.library && pos.id.startsWith("lib-")) {
                    const libIdNum = Number(pos.id.replace(/^lib-/, ""));
                    const lp = libraryPositions.find((l) => l.id === libIdNum);
                    if (lp) libInfo = pickLibraryEpInfo(lp, pos.qty);
                  }
                  const isManualEp = manualEpIds.has(pos.id);
                  return (
                    <div key={pos.id} data-lv-pos-id={pos.id} className={`border-t border-border/50 transition-colors ${pos.variant ? "bg-amber-50/40 dark:bg-amber-950/20 border-l-2 border-l-amber-400" : pos.aktiv ? "bg-emerald-50/30 dark:bg-emerald-950/10" : ""} ${highlightedId === pos.id ? "ring-2 ring-inset ring-amber-400 bg-amber-50/60 dark:bg-amber-950/30" : ""}`}>
                      <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_4.5rem_3.5rem_4.5rem_4.5rem_2rem] gap-1 px-3 py-2 items-center">
                        <input
                          type="checkbox"
                          checked={pos.aktiv}
                          onChange={() => togglePosition(sek.id, pos.id)}
                          className="w-4 h-4 rounded accent-emerald-600 cursor-pointer"
                          data-testid={`lv-aktiv-${pos.id}`}
                        />

                        <div className="flex items-center gap-1 min-w-0">
                          <input
                            type="text"
                            value={pos.name}
                            onChange={(e) => aendereName(sek.id, pos.id, e.target.value)}
                            className={`flex-1 min-w-0 text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
                              pos.aktiv ? "text-foreground" : "text-muted-foreground"
                            }`}
                            data-testid={`lv-name-${pos.id}`}
                          />
                          {pos.tag && (
                            <span
                              className={`text-[9px] px-1 py-0.5 rounded font-medium shrink-0 ${
                                pos.tag === "AZ"
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {pos.tag}
                            </span>
                          )}
                          {pos.variant && (
                            <span
                              className="text-[9px] px-1 py-0.5 rounded font-medium shrink-0 bg-amber-100 text-amber-800"
                              title="Variante – wird nicht zur Summe gezählt"
                            >
                              VARIANTE
                            </span>
                          )}
                          {pos.library && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded font-medium shrink-0 bg-sky-100 text-sky-800"
                              title="Aus Positions-Bibliothek"
                            >
                              <BookOpen className="w-2.5 h-2.5" />
                              BIBLIOTHEK
                            </span>
                          )}
                          {libInfo && !isManualEp && (() => {
                            const label =
                              libInfo.source === "sample" && libInfo.matchedMenge !== null
                                ? `~${libInfo.matchedMenge.toLocaleString("de-DE")} ${pos.unit}`
                                : libInfo.source === "median"
                                  ? `Median ${libInfo.sampleCount}P`
                                  : "Standard";
                            return (
                              <span
                                className="text-[9px] px-1 py-0.5 rounded font-medium shrink-0 bg-emerald-100 text-emerald-800"
                                title={`EP aus Bibliothek (mengenabhängig). Quelle: ${label}, ${libInfo.sampleCount} Vergleichswert(e).`}
                              >
                                EP DB · {label}
                              </span>
                            );
                          })()}
                          {pos.library && isManualEp && (
                            <span
                              className="text-[9px] px-1 py-0.5 rounded font-medium shrink-0 bg-amber-100 text-amber-800"
                              title="Einzelpreis manuell überschrieben – keine automatische Anpassung mehr."
                            >
                              EP MANUELL
                            </span>
                          )}
                          {(() => {
                            const plausi = plausibilityByPosId[pos.id];
                            if (!plausi || plausi.count <= 0) return null;
                            const tooltipLines = [
                              plausi.count === 1
                                ? "1 Plausibilitätshinweis:"
                                : `${plausi.count} Plausibilitätshinweise:`,
                              ...plausi.titles.map((t) => `• ${t}`),
                              "",
                              "Klicken: in der Plausibilitätsprüfung öffnen.",
                            ];
                            return (
                              <button
                                type="button"
                                onClick={() => onJumpToPlausibility?.(pos.id)}
                                title={tooltipLines.join("\n")}
                                aria-label={`${plausi.count} Plausibilitätshinweis${plausi.count === 1 ? "" : "e"} – in Plausibilitätsprüfung öffnen`}
                                className="inline-flex items-center gap-0.5 shrink-0 rounded px-1 py-0.5 text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-300 dark:hover:bg-amber-900/40 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors"
                                data-testid={`lv-plausi-jump-${pos.id}`}
                                data-plausi-count={plausi.count}
                              >
                                <AlertTriangle className="w-3.5 h-3.5" />
                                {plausi.count > 1 && (
                                  <span className="text-[9px] font-semibold tabular-nums">
                                    {plausi.count}
                                  </span>
                                )}
                              </button>
                            );
                          })()}
                        </div>

                        <GermanNumberInput
                          value={pos.qty}
                          min={0}
                          maxFractionDigits={2}
                          disabled={!pos.aktiv}
                          onValueChange={(n) => {
                            aendereMenge(sek.id, pos.id, String(n));
                            if (ref) setMengenRefs(prev => { const next = { ...prev }; delete next[pos.id]; return next; });
                            onMengeManuallyChanged?.(pos.id);
                          }}
                          className="w-full text-right text-xs py-1 px-1.5 rounded border border-input bg-background disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          data-testid={`lv-qty-${pos.id}`}
                        />

                        <UnitCell
                          value={pos.unit}
                          onChange={(val) => aendereUnit(sek.id, pos.id, val)}
                          testId={`lv-unit-${pos.id}`}
                        />

                        <GermanNumberInput
                          value={pos.ep}
                          min={0}
                          maxFractionDigits={2}
                          minFractionDigits={2}
                          disabled={!pos.aktiv}
                          onValueChange={(n) => aendereEP(sek.id, pos.id, String(n))}
                          className="w-full text-right text-xs py-1 px-1.5 rounded border border-input bg-background disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          data-testid={`lv-ep-${pos.id}`}
                        />

                        <div
                          className={`text-xs text-right tabular-nums font-medium ${
                            pos.variant
                              ? "text-amber-700 line-through opacity-70"
                              : pos.aktiv ? "text-foreground" : "text-muted-foreground"
                          }`}
                          title={pos.variant ? "Variante – nicht in Summe enthalten" : undefined}
                        >
                          {pos.aktiv ? `\u20AC ${euFormat(gp)}` : "\u2013"}
                        </div>

                        <button
                          type="button"
                          onClick={() => positionEntfernen(sek.id, pos.id)}
                          title="Position entfernen"
                          className="text-muted-foreground hover:text-destructive p-1 rounded transition-colors"
                          data-testid={`lv-remove-${pos.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {(pos.id.startsWith("custom-") || (typeof pos.beschreibung === "string" && pos.beschreibung.length > 0)) && (
                        <div className="px-3 pb-2">
                          <details
                            className="group"
                            open={
                              typeof pos.beschreibung === "string" &&
                              pos.beschreibung.length > 0 &&
                              !collapsedBeschreibung.has(pos.id)
                            }
                            onToggle={(e) => {
                              const isOpen = (e.currentTarget as HTMLDetailsElement).open;
                              setCollapsedBeschreibung((prev) => {
                                const has = prev.has(pos.id);
                                if (isOpen && has) {
                                  const next = new Set(prev);
                                  next.delete(pos.id);
                                  return next;
                                }
                                if (!isOpen && !has) {
                                  const next = new Set(prev);
                                  next.add(pos.id);
                                  return next;
                                }
                                return prev;
                              });
                            }}
                          >
                            <summary className="flex items-center justify-between cursor-pointer text-[10px] uppercase tracking-wider text-sky-700 hover:text-sky-800 select-none">
                              <span>Beschreibung</span>
                              {typeof pos.beschreibung === "string" && pos.beschreibung.length > 0 && (
                                <span className="normal-case text-[10px] text-muted-foreground">
                                  {pos.beschreibung.length} Zeichen
                                </span>
                              )}
                            </summary>
                            <textarea
                              value={pos.beschreibung ?? ""}
                              onChange={(e) => aendereBeschreibung(sek.id, pos.id, e.target.value)}
                              placeholder="Beschreibungstext (z.B. aus Foto-Analyse übernommen) – wird im PDF unter der Position angezeigt."
                              rows={Math.min(8, Math.max(2, ((pos.beschreibung ?? "").match(/\n/g)?.length ?? 0) + 2))}
                              className="mt-1 w-full text-xs leading-snug py-1.5 px-2 rounded border border-sky-200 bg-sky-50/40 dark:bg-sky-950/20 focus:outline-none focus:ring-1 focus:ring-sky-500 whitespace-pre-wrap"
                              data-testid={`lv-beschreibung-${pos.id}`}
                            />
                          </details>
                        </div>
                      )}

                      {breakdown.length > 0 && (
                        <div className="px-3 pb-2">
                          <details className="group" open>
                            <summary className="flex items-center justify-between cursor-pointer text-[10px] uppercase tracking-wider text-emerald-700 hover:text-emerald-800 select-none">
                              <span>Foto-Aufschlüsselung ({breakdown.length})</span>
                              <span className="tabular-nums normal-case text-[11px] text-emerald-700">
                                Σ {breakdownSum.toFixed(2)} {pos.unit}
                                {isOverridden && (
                                  <span className="ml-1 text-amber-700">· LV: {pos.qty.toFixed(2)} (manuell)</span>
                                )}
                              </span>
                            </summary>
                            <div className="mt-1 rounded border border-emerald-200 bg-emerald-50/40 divide-y divide-emerald-100">
                              {breakdown.map((entry) => {
                                const overridden = entry.berechneteMenge !== undefined &&
                                  Math.abs((entry.berechneteMenge ?? 0) - entry.qty) > 0.005;
                                return (
                                  <div
                                    key={entry.photoId}
                                    className="flex items-center justify-between gap-2 px-2 py-1 text-[11px]"
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      {entry.previewUrl && (
                                        <button
                                          type="button"
                                          onClick={() => onGoToMeasurementPhoto?.(entry.photoIndex)}
                                          className="shrink-0 rounded overflow-hidden border border-emerald-200 hover:border-emerald-400 transition-colors focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                          title={entry.photoName}
                                          data-testid={`lv-breakdown-thumb-${entry.photoId}`}
                                        >
                                          <img
                                            src={entry.previewUrl}
                                            alt={entry.photoName}
                                            className="w-8 h-8 object-cover block"
                                            loading="lazy"
                                          />
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => onGoToMeasurementPhoto?.(entry.photoIndex)}
                                        className="truncate text-left text-emerald-800 hover:underline min-w-0"
                                        title={entry.photoName}
                                      >
                                        Foto {entry.photoIndex + 1}: {entry.photoName}
                                      </button>
                                    </div>
                                    <span className="tabular-nums shrink-0 text-emerald-900">
                                      {entry.berechneteMenge !== undefined && (
                                        <span className="text-muted-foreground mr-1">
                                          berechnet {entry.berechneteMenge.toFixed(2)} ·
                                        </span>
                                      )}
                                      <span className={overridden ? "font-semibold text-amber-700" : "font-semibold"}>
                                        übernommen {entry.qty.toFixed(2)} {pos.unit}
                                      </span>
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        </div>
                      )}

                      {availableMengen.length > 0 && (
                        <div className="px-3 pb-2 flex flex-col gap-1">
                          {ref ? (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {onGoToMeasurementPhoto ? (
                                <button
                                  type="button"
                                  onClick={() => onGoToMeasurementPhoto(ref.imageIndex)}
                                  title="Zum Quellbild springen"
                                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-400 transition-colors cursor-pointer"
                                >
                                  <Link2 className="w-2.5 h-2.5" />
                                  {ref.sourceLabel}
                                </button>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-emerald-700 border-emerald-300 bg-emerald-50 gap-1">
                                  <Link2 className="w-2.5 h-2.5" />
                                  {ref.sourceLabel}
                                </Badge>
                              )}
                              <button
                                type="button"
                                onClick={() => setShowPicker(pickerOpen ? null : pos.id)}
                                className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                              >
                                {pickerOpen ? "Schließen" : "Ändern"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setMengenRefs(prev => { const next = { ...prev }; delete next[pos.id]; return next; })}
                                className="text-[10px] text-muted-foreground hover:text-destructive underline underline-offset-2"
                              >
                                Entfernen
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setShowPicker(pickerOpen ? null : pos.id)}
                              className="flex items-center gap-1 text-[10px] text-emerald-700 hover:text-emerald-800 w-fit"
                            >
                              <Link2 className="w-3 h-3" />
                              Menge aus Aufmaß übernehmen
                            </button>
                          )}

                          {pickerOpen && (
                            <div className="mt-1 rounded border border-border bg-card shadow-sm">
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 pt-1.5 pb-1 border-b border-border">
                                Verfügbare Aufmaßmengen
                              </div>
                              {availableMengen.map((am) => (
                                <button
                                  key={`${am.imageIndex}-${am.id}`}
                                  type="button"
                                  onClick={() => {
                                    aendereMenge(sek.id, pos.id, String(am.value));
                                    if (pos.aktiv === false) togglePosition(sek.id, pos.id);
                                    setMengenRefs(prev => ({
                                      ...prev,
                                      [pos.id]: {
                                        mengeId: am.id,
                                        imageIndex: am.imageIndex,
                                        sourceLabel: `${am.label} (${am.imageName}) · ${am.value} ${am.unit}`,
                                      },
                                    }));
                                    setShowPicker(null);
                                  }}
                                  className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
                                >
                                  <div className="flex flex-col">
                                    <span className="text-xs font-medium">{am.label}</span>
                                    <span className="text-[10px] text-muted-foreground">{am.imageName}</span>
                                  </div>
                                  <span className="text-xs tabular-nums text-emerald-700 font-medium ml-2 shrink-0">
                                    {am.value} {am.unit}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {!pos.variant && (
                        <VarianteHinzufuegenButton
                          originalUnit={originalUnit}
                          currentUnit={pos.unit}
                          onDuplicate={(orig, neu) => positionDuplizierenMitEinheit(sek.id, pos.id, orig, neu)}
                          testId={`lv-variant-${pos.id}`}
                        />
                      )}
                    </div>
                  );
                })}

                <AddPositionForm
                  sektionId={sek.id}
                  onAdd={(payload) => positionHinzufuegen(sek.id, payload)}
                  currentProjectId={currentProjectId}
                  libraryPositions={libraryPositions}
                  activeLibIds={activeLibIds}
                  onAddFromLibrary={addLibraryPositionById}
                  // Bibliotheks-/Vorlagen-Picker auf die sichtbaren Sektionen
                  // einschränken, damit der Nutzer in Schritt 4 (nur s7)
                  // keine Items aus ausgeblendeten Sektionen auswählen kann,
                  // die danach unsichtbar im LV landen würden.
                  allowedSektionIds={visibleSektionIds}
                />
              </div>
            )}
          </div>
        );
      })}

      <div className="rounded-xl border border-border bg-card p-4 space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Nettobetrag</span>
          <span className="tabular-nums font-medium">{"\u20AC"} {euFormat(summen.netto)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">MwSt. 20%</span>
          <span className="tabular-nums">{"\u20AC"} {euFormat(summen.mwst)}</span>
        </div>
        <div className="flex justify-between text-base font-medium border-t border-border pt-2 mt-2">
          <span>Bruttobetrag</span>
          <span className="tabular-nums text-emerald-700">{"\u20AC"} {euFormat(summen.brutto)}</span>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={allesZuruecksetzen}
          className="text-xs text-muted-foreground hover:text-destructive transition-colors underline underline-offset-2"
        >
          Alle Positionen zur\u00FCcksetzen
        </button>
      </div>
    </div>
  );
}

function AddPositionForm({
  sektionId,
  onAdd,
  currentProjectId,
  libraryPositions,
  activeLibIds,
  onAddFromLibrary,
  allowedSektionIds,
}: {
  sektionId: string;
  onAdd: (payload: { name: string; unit: string; ep: number; qty: number; tag: string | null; saveToLibrary: boolean }) => void;
  currentProjectId?: number;
  libraryPositions: LibraryPosition[];
  activeLibIds: Set<number>;
  onAddFromLibrary: (libId: number) => void;
  /**
   * Optionaler Whitelist-Filter (siehe `LVKalkulatorProps.visibleSektionIds`).
   * Wenn gesetzt, werden Bibliotheks-/Vorlagen-Picker nur Items aus diesen
   * Sektionen anzeigen — verhindert, dass Items unsichtbar in ausgeblendete
   * Sektionen einsortiert werden.
   */
  allowedSektionIds?: string[];
}) {
  const filteredLibraryPositions = allowedSektionIds
    ? libraryPositions.filter((lp) => allowedSektionIds.includes(lp.sektionId))
    : libraryPositions;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("Stk");
  const [ep, setEp] = useState("0");
  const [qty, setQty] = useState("1");
  const [saveLib, setSaveLib] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);

  const reset = () => {
    setName("");
    setUnit("Stk");
    setEp("0");
    setQty("1");
    setSaveLib(false);
    setOpen(false);
  };

  const applyTemplate = (tpl: CustomPositionTemplate) => {
    setName(tpl.name);
    setUnit(tpl.unit);
    setEp(String(tpl.ep));
    setQty(String(tpl.qty ?? 1));
    setSaveLib(false);
    setOpen(true);
    setPickerOpen(false);
  };

  if (!open) {
    return (
      <div className="border-t border-border/50 px-3 py-2 bg-muted/20 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex-1 flex items-center justify-center gap-2 text-sm py-2 px-3 rounded-md border border-dashed border-emerald-400 bg-emerald-50/60 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-500 hover:text-emerald-800 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/40 font-medium transition-colors"
          data-testid={`lv-add-${sektionId}`}
        >
          <Plus className="w-4 h-4" />
          Position hinzufügen
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex items-center justify-center gap-2 text-sm py-2 px-3 rounded-md border border-dashed border-sky-400 bg-sky-50/60 text-sky-700 hover:bg-sky-100 hover:border-sky-500 hover:text-sky-800 dark:bg-sky-950/20 dark:hover:bg-sky-950/40 font-medium transition-colors"
          data-testid={`lv-add-from-project-${sektionId}`}
        >
          <FolderOpen className="w-4 h-4" />
          Aus Projekt übernehmen
        </button>
        <button
          type="button"
          onClick={() => setLibraryPickerOpen(true)}
          className="flex items-center justify-center gap-2 text-sm py-2 px-3 rounded-md border border-dashed border-sky-400 bg-sky-50/60 text-sky-700 hover:bg-sky-100 hover:border-sky-500 hover:text-sky-800 dark:bg-sky-950/20 dark:hover:bg-sky-950/40 font-medium transition-colors"
          data-testid={`lv-add-from-library-${sektionId}`}
        >
          <BookOpen className="w-4 h-4" />
          Aus Bibliothek hinzufügen
        </button>
        <CustomPositionTemplatePicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onPick={applyTemplate}
          currentProjectId={currentProjectId}
        />
        <LibraryPositionPicker
          open={libraryPickerOpen}
          onOpenChange={setLibraryPickerOpen}
          libraryPositions={filteredLibraryPositions}
          activeLibIds={activeLibIds}
          onPick={(libId) => {
            onAddFromLibrary(libId);
            setLibraryPickerOpen(false);
          }}
          preferredSektionId={sektionId}
        />
      </div>
    );
  }

  return (
    <div className="border-t border-border/50 px-3 py-3 bg-emerald-50/40 dark:bg-emerald-950/10 space-y-2">
      <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_3.5rem_4rem] gap-1">
        <input
          type="text"
          autoFocus
          placeholder="Bezeichnung"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
          data-testid={`lv-add-name-${sektionId}`}
        />
        <input
          type="number"
          step="0.01"
          placeholder="Menge"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="text-right text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
        <input
          type="text"
          placeholder="Einh."
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className="text-right text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
        <input
          type="number"
          step="0.01"
          placeholder="EP"
          value={ep}
          onChange={(e) => setEp(e.target.value)}
          className="text-right text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={saveLib}
            onChange={(e) => setSaveLib(e.target.checked)}
            className="w-3.5 h-3.5 rounded accent-sky-600 cursor-pointer"
            data-testid={`lv-add-savelib-${sektionId}`}
          />
          <BookOpen className="w-3 h-3" />
          Auch in Bibliothek speichern
        </label>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={reset}
            className="text-xs px-2 py-1 rounded text-muted-foreground hover:text-foreground"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => {
              onAdd({
                name,
                unit,
                ep: parseFloat(ep) || 0,
                qty: parseFloat(qty) || 0,
                tag: null,
                saveToLibrary: saveLib,
              });
              reset();
            }}
            className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium"
            data-testid={`lv-add-submit-${sektionId}`}
          >
            Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomPositionTemplatePicker({
  open,
  onOpenChange,
  onPick,
  currentProjectId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onPick: (tpl: CustomPositionTemplate) => void;
  currentProjectId?: number;
}) {
  const [search, setSearch] = useState("");

  const { data: templates = [], isLoading, isError } = useQuery<CustomPositionTemplate[]>({
    queryKey: ["/api/templates/custom-positions", currentProjectId ?? null],
    queryFn: async () => {
      const url = currentProjectId
        ? `/api/templates/custom-positions?excludeProjectId=${currentProjectId}`
        : "/api/templates/custom-positions";
      const res = await apiRequest("GET", url);
      return (await res.json()) as CustomPositionTemplate[];
    },
    enabled: open,
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      t.unit.toLowerCase().includes(q) ||
      t.projectName.toLowerCase().includes(q)
    );
  }, [templates, search]);

  const grouped = useMemo(() => {
    const byProject = new Map<string, CustomPositionTemplate[]>();
    for (const t of filtered) {
      const key = `${t.projectId}::${t.projectName}`;
      const arr = byProject.get(key) ?? [];
      arr.push(t);
      byProject.set(key, arr);
    }
    return Array.from(byProject.entries()).map(([key, items]) => {
      const [pid, name] = key.split("::");
      return { projectId: pid, projectName: name, items };
    });
  }, [filtered]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Position aus anderem Projekt übernehmen</DialogTitle>
          <DialogDescription>
            Eigene Positionen aus deinen anderen Projekten als Vorlage. Nach
            Auswahl kannst du Bezeichnung, Einheit, Menge und Einzelpreis
            beliebig anpassen — die Quelle bleibt unverändert.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Bezeichnung, Einheit oder Projekt…"
            className="w-full pl-8 pr-8 py-2 text-sm rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-sky-500"
            data-testid="lv-template-picker-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4">
          {isLoading && (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Vorlagen werden geladen…
            </div>
          )}
          {isError && (
            <div className="text-sm text-destructive py-8 text-center">
              Vorlagen konnten nicht geladen werden.
            </div>
          )}
          {!isLoading && !isError && grouped.length === 0 && (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {templates.length === 0
                ? "Es gibt noch keine eigenen Positionen in anderen Projekten."
                : "Kein Treffer für die Suche."}
            </div>
          )}
          {grouped.map((g) => (
            <div key={`${g.projectId}-${g.projectName}`} className="space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium px-1">
                {g.projectName}
              </div>
              <div className="rounded border border-border divide-y divide-border">
                {g.items.map((t, i) => {
                  const sek = STAMMDATEN.find((s) => s.id === t.sektionId);
                  const sekLabel = sek ? `${sek.icon} ${sek.titel}` : t.sektionId;
                  return (
                    <button
                      key={`${t.projectId}-${t.sektionId}-${i}-${t.name}`}
                      type="button"
                      onClick={() => onPick(t)}
                      className="w-full grid grid-cols-[minmax(0,1fr)_4rem_5rem_5rem] gap-2 items-center px-3 py-2 text-left text-sm hover:bg-sky-50 dark:hover:bg-sky-950/30 transition-colors"
                      data-testid={`lv-template-item-${t.projectId}-${i}`}
                    >
                      <div className="min-w-0">
                        <div className="truncate">
                          <span className="font-medium">{t.name}</span>
                          {t.tag && (
                            <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded font-medium bg-amber-100 text-amber-800 align-middle">
                              {t.tag}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                          Sektion: {sekLabel}
                        </div>
                      </div>
                      <div className="text-right text-xs tabular-nums">{t.qty}</div>
                      <div className="text-right text-xs text-muted-foreground">{t.unit}</div>
                      <div className="text-right text-xs tabular-nums text-emerald-700 font-medium">
                        {"\u20AC"} {euFormat(t.ep)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LibraryPositionPicker({
  open,
  onOpenChange,
  libraryPositions,
  activeLibIds,
  onPick,
  preferredSektionId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  libraryPositions: LibraryPosition[];
  activeLibIds: Set<number>;
  onPick: (libId: number) => void;
  preferredSektionId: string;
}) {
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editUnit, setEditUnit] = useState("");
  const [editEp, setEditEp] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<LibraryPosition | null>(null);
  const { toast } = useToast();

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: { name: string; unit: string; ep: number } }) => {
      const res = await apiRequest("PATCH", `/api/library-positions/${id}`, patch);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/library-positions"] });
      toast({ description: "Position aktualisiert." });
      setEditingId(null);
    },
    onError: (err: any) => {
      toast({
        variant: "destructive",
        description: err?.message ?? "Aktualisieren fehlgeschlagen.",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/library-positions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/library-positions"] });
      toast({ description: "Position aus Bibliothek entfernt." });
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast({
        variant: "destructive",
        description: err?.message ?? "Löschen fehlgeschlagen.",
      });
    },
  });

  const startEdit = (lp: LibraryPosition) => {
    setEditingId(lp.id);
    setEditName(lp.name);
    setEditUnit(lp.unit);
    setEditEp(String(lp.ep));
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = (id: number) => {
    const name = editName.trim();
    const unit = editUnit.trim();
    const ep = parseFloat(editEp.replace(",", "."));
    if (!name) {
      toast({ variant: "destructive", description: "Bezeichnung darf nicht leer sein." });
      return;
    }
    if (!unit) {
      toast({ variant: "destructive", description: "Einheit darf nicht leer sein." });
      return;
    }
    if (!Number.isFinite(ep) || ep < 0) {
      toast({ variant: "destructive", description: "EP muss eine Zahl ≥ 0 sein." });
      return;
    }
    updateMutation.mutate({ id, patch: { name, unit, ep: Math.round(ep * 100) / 100 } });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const own = libraryPositions.filter((lp) => lp.aktiv !== false);
    if (!q) return own;
    return own.filter(
      (lp) =>
        lp.name.toLowerCase().includes(q) ||
        lp.unit.toLowerCase().includes(q) ||
        (lp.sektionId ?? "").toLowerCase().includes(q),
    );
  }, [libraryPositions, search]);

  const grouped = useMemo(() => {
    const bySek = new Map<string, LibraryPosition[]>();
    for (const lp of filtered) {
      const key = lp.sektionId ?? "_";
      const arr = bySek.get(key) ?? [];
      arr.push(lp);
      bySek.set(key, arr);
    }
    const sortedKeys = Array.from(bySek.keys()).sort((a, b) => {
      if (a === preferredSektionId) return -1;
      if (b === preferredSektionId) return 1;
      return a.localeCompare(b);
    });
    return sortedKeys.map((k) => {
      const sek = STAMMDATEN.find((s) => s.id === k);
      return {
        sektionId: k,
        sektionLabel: sek ? `${sek.icon} ${sek.titel}` : k,
        items: (bySek.get(k) ?? []).sort((a, b) => a.name.localeCompare(b.name)),
      };
    });
  }, [filtered, preferredSektionId]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setEditingId(null);
          setDeleteTarget(null);
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Position aus Bibliothek hinzufügen</DialogTitle>
          <DialogDescription>
            Wähle eine Position aus deiner Wissensdatenbank. Bereits aktive
            Positionen sind markiert und werden nicht doppelt hinzugefügt –
            erneutes Auswählen bleibt ohne Effekt. Eigene Einträge kannst du
            direkt hier umbenennen oder entfernen.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Bezeichnung, Einheit oder Sektion…"
            className="w-full pl-8 pr-8 py-2 text-sm rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-sky-500"
            data-testid="lv-library-picker-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4">
          {grouped.length === 0 && (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {libraryPositions.length === 0
                ? "Deine Positions-Bibliothek ist noch leer."
                : "Kein Treffer für die Suche."}
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.sektionId} className="space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium px-1">
                {g.sektionLabel}
              </div>
              <div className="rounded border border-border divide-y divide-border">
                {g.items.map((lp) => {
                  const already = activeLibIds.has(lp.id);
                  const editable = lp.userId !== null;
                  const isEditing = editingId === lp.id;
                  const samples = lp.priceSamples ?? [];
                  const mengen = samples.map((s) => s.menge).filter((m) => m > 0);
                  const range =
                    mengen.length >= 2
                      ? `~${Math.min(...mengen).toLocaleString("de-DE")}–${Math.max(...mengen).toLocaleString("de-DE")} ${lp.unit}`
                      : samples.length > 0
                        ? `${samples.length} Werte`
                        : "Standard";

                  if (isEditing) {
                    return (
                      <div
                        key={lp.id}
                        className="px-3 py-2 bg-amber-50/40 dark:bg-amber-950/10 space-y-2"
                        data-testid={`lv-library-item-edit-${lp.id}`}
                      >
                        <div className="grid grid-cols-[minmax(0,1fr)_5rem_5rem] gap-2 items-center">
                          <input
                            type="text"
                            autoFocus
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Bezeichnung"
                            className="text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-sky-500"
                            data-testid={`lv-library-edit-name-${lp.id}`}
                          />
                          <input
                            type="text"
                            value={editUnit}
                            onChange={(e) => setEditUnit(e.target.value)}
                            placeholder="Einh."
                            className="text-right text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-sky-500"
                            data-testid={`lv-library-edit-unit-${lp.id}`}
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={editEp}
                            onChange={(e) => setEditEp(e.target.value)}
                            placeholder="EP"
                            className="text-right text-xs py-1 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-sky-500"
                            data-testid={`lv-library-edit-ep-${lp.id}`}
                          />
                        </div>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="text-xs px-2 py-1 rounded text-muted-foreground hover:text-foreground"
                            data-testid={`lv-library-edit-cancel-${lp.id}`}
                          >
                            Abbrechen
                          </button>
                          <button
                            type="button"
                            disabled={updateMutation.isPending}
                            onClick={() => saveEdit(lp.id)}
                            className="text-xs px-3 py-1 rounded bg-sky-600 text-white hover:bg-sky-700 font-medium disabled:opacity-50"
                            data-testid={`lv-library-edit-save-${lp.id}`}
                          >
                            {updateMutation.isPending ? "Speichern…" : "Speichern"}
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={lp.id}
                      className={`grid grid-cols-[minmax(0,1fr)_5rem_5rem_auto] gap-2 items-center px-3 py-2 text-sm transition-colors ${already ? "opacity-50 bg-muted/30" : "hover:bg-sky-50/60 dark:hover:bg-sky-950/20"}`}
                      data-testid={`lv-library-item-${lp.id}`}
                    >
                      <button
                        type="button"
                        disabled={already}
                        onClick={() => onPick(lp.id)}
                        className={`min-w-0 text-left col-start-1 col-end-4 grid grid-cols-subgrid gap-2 items-center ${already ? "cursor-not-allowed" : "cursor-pointer"}`}
                        data-testid={`lv-library-pick-${lp.id}`}
                      >
                        <div className="min-w-0">
                          <div className="truncate">
                            <span className="font-medium">{lp.name}</span>
                            {already && (
                              <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded font-medium bg-emerald-100 text-emerald-800 align-middle">
                                BEREITS AKTIV
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                            {lp.unit} · {range}
                            {!editable && (
                              <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-slate-200 text-slate-700 align-middle">
                                STANDARD
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">{lp.unit}</div>
                        <div className="text-right text-xs tabular-nums text-emerald-700 font-medium">
                          {"\u20AC"} {euFormat(lp.ep)}
                        </div>
                      </button>
                      {editable && (
                        <div className="flex items-center gap-0.5 pl-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEdit(lp);
                            }}
                            className="p-1 rounded text-muted-foreground hover:text-sky-700 hover:bg-sky-100/60 dark:hover:bg-sky-950/40"
                            title="Position bearbeiten"
                            aria-label="Position bearbeiten"
                            data-testid={`lv-library-edit-${lp.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(lp);
                            }}
                            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            title="Position löschen"
                            aria-label="Position löschen"
                            data-testid={`lv-library-delete-${lp.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <AlertDialog
          open={deleteTarget !== null}
          onOpenChange={(v) => {
            if (!v) setDeleteTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Position aus Bibliothek entfernen?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget && (
                  <>
                    „{deleteTarget.name}" wird aus deiner Bibliothek gelöscht.
                    Aktive LV-Zeilen, die auf diese Position verweisen, bleiben
                    erhalten, verlieren aber beim nächsten Laden den
                    Bibliotheks-Bezug.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="lv-library-delete-cancel">
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="lv-library-delete-confirm"
              >
                {deleteMutation.isPending ? "Löschen…" : "Löschen"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

